<template>
    <v-dialog
        v-model="show"
        :max-width="type === 'identity' ? 900 : 450"
        persistent
    >
        <v-card
            :loading="loading"
        >
            <v-card-title class="headline">
                {{ type === 'identity'
                    ? 'Identity Verification'
                    : 'Address Verification'
                }}
            </v-card-title>
            <v-card-text class="py-0">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-if="type === 'identity'"
                                v-model="id_number"
                                v-validate="'required'"
                                label="ID Document Number"
                                outlined
                                class="pb-0 pb-lg-3"
                                name="id_number"
                                type="text"
                                hide-details="auto"
                                required
                                :error-messages="errors.first('id_number')"
                            />
                        </v-col>

                        <v-col cols="12">
                            <h3 class="text-h6">
                                ID expiry date
                            </h3>
                        </v-col>

                        <v-col
                            v-if="type === 'identity'"
                            cols="12"
                            sm="6"
                            md="3"
                        >
                            <v-select
                                v-model="expiry_month"
                                v-validate="'required'"
                                :items="months"
                                label="Month"
                                name="month"
                                hide-details="auto"
                                outlined
                                :error-messages="errors.first('month')"
                            />
                        </v-col>
                        <v-col
                            v-if="type === 'identity'"
                            cols="12"
                            sm="6"
                            md="3"
                        >
                            <v-select
                                v-model="expiry_year"
                                v-validate="'required'"
                                :items="years"
                                label="Year"
                                name="year"
                                hide-details="auto"
                                outlined
                                :error-messages="errors.first('month')"
                            />
                        </v-col>

                        <v-col
                            v-if="type === 'identity'"
                            cols="6"
                        />

                        <v-col
                            cols="12"
                            :md="type === 'identity' ? 6 : 12"
                        >
                            <FileInputPreview
                                v-model="doc_file"
                                v-validate="'required|image|size:2048'"
                                name="doc_file"
                                :label="type === 'identity'
                                    ? 'ID document'
                                    : 'Document'"
                                :hint="type === 'identity'
                                    ? 'Upload an image of your ID document'
                                    : 'Upload an image of you document'"
                                accept="image/png, image/jpeg, image/bmp"
                                prepend-icon="mdi-camera"
                                :error-messages="errors.first('doc_file')"
                                persistent-hint
                            />
                        </v-col>

                        <v-col
                            v-if="type === 'identity'"
                            cols="12"
                            md="6"
                        >
                            <FileInputPreview
                                v-model="photo_file"
                                v-validate="'required|image|size:2048'"
                                name="photo_file"
                                label="Selfie"
                                hint="Upload an image of your selfie"
                                accept="image/png, image/jpeg, image/bmp"
                                prepend-icon="mdi-camera"
                                :error-messages="errors.first('photo_file')"
                                persistent-hint
                            />
                        </v-col>

                        <v-col
                            cols="12"
                        >
                            <FileInputPreview
                                v-model="extra_file"
                                v-validate="'image|size:2048'"
                                name="extra_file"
                                label="Business Document"
                                hint="Upload an Image of your Business Document (e.g. Certificate of Incorporation, etc)"
                                accept="image/png, image/jpeg, image/bmp"
                                prepend-icon="mdi-camera"
                                :error-messages="errors.first('extra_file')"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="primary"
                    text
                    @click="close"
                >
                    Close
                </v-btn>
                <v-btn
                    color="primary white"
                    class="white--text"
                    @click="save"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    /* eslint-disable camelcase */
    import { range } from 'lodash';
    import FileInputPreview from '../../components/file-input-preview.vue';

    export default {
        components: {
            FileInputPreview,
        },
        props: {
            value: {
                type: Boolean,
                default: false,
            },
            type: {
                type: String,
                default: 'identity',
            },
        },
        data: () => ({
            loading: false,
            id_number: null,
            doc_file: null,
            photo_file: null,
            expiry_month: null,
            expiry_year: null,
            extra_file: null,
        }),
        computed: {
            show: {
                get() {
                    return this.value;
                },
                set(val) {
                    this.$emit('input', val);
                },
            },
            currentMonth() {
                return (new Date()).getMonth() + 1;
            },
            currentYear() {
                return (new Date()).getFullYear();
            },
            months() {
                if (this.expiry_year === this.currentYear) {
                    return range(this.currentMonth, 13);
                }
                return range(1, 13);
            },
            years() {
                const year = new Date().getFullYear();
                return range(year, year + 51);
            },
        },
        methods: {
            range: (start, end) => Array(end - start + 1).fill().map((_, idx) => start + idx),
            close() {
                this.show = false;
                this.id_number = null;
                this.doc_file = null;
                this.photo_file = null;
                this.extra_file = null;
                this.errors.clear();
            },
            save() {
                this.$validator.validateAll().then((isValid) => {
                    if (isValid) {
                        this.submit();
                    }
                });
            },
            async submit() {
                const {
                    type,
                    id_number,
                    doc_file,
                    photo_file,
                    expiry_month,
                    expiry_year,
                    extra_file,
                } = this;

                const data = JSON.stringify({
                    id_number,
                    expiry_month,
                    expiry_year,
                });

                const payload = {
                    type,
                    doc_file,
                    ...(type === 'identity'
                        ? {
                            photo_file,
                            data,
                        }
                        : {}
                    ),
                    extra_file,
                };

                try {
                    this.loading = true;

                    await Vue.backendApi() // eslint-disable-line no-undef
                        .docVerification()
                        .create(payload);

                    this.$swal({
                        type: 'success',
                        title: 'Thank You!',
                        text: 'Your Document will be verified within the next 24 hours',
                    });
                    this.close();
                } catch (error) {
                    this.$swal({
                        type: 'error',
                        title: 'There was an error',
                        text: error,
                    });
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>
